/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import AdobeMetricsRuntime from './AdobeMetricsRuntime';
import {Level, Metric, RecordType} from '@exc/metrics';
import {METRICS_VERSION as sdkVersion} from './BuildVersion';
import uuidv1 from 'uuid/v1';

declare const adobeMetrics: AdobeMetricsRuntime;
const LOGGER_NAME = 'exc.metrics-browser-runtime.network';

function instrumentRequest(dateNow: number, correlationId: string, ...args: any): any {
  const m = {
    data: {
      url: args[0]
    },
    dateNow,
    level: Level.INFO,
    metricsState: {
      correlationId,
      sdkVersion
    },
    name: LOGGER_NAME,
    recordType: RecordType.NETWORK_REQUEST
  } as Metric;

  // args[2] and beyond could be rest args to fetch(), so we'll put
  // them into an args[] array on data
  // doing this first in case args[1] has an args member (unlikely)
  if (args.length > 2) {
    m.data.args = args.slice(2);
  }

  // arg[1] is special because its the second parameter to fetch()
  // so we copy it's content to the data object for ease of access
  args && Object.assign(m.data, args[1]);

  adobeMetrics.write(m);
  return args;
}

function instrumentResponse(start: number, correlationId: string, response: any): any {
  const now = Date.now();
  const m = {
    data: Object.assign({}, response, {
      headers: {},
      ok: response.ok,
      redirected: response.redirected,
      status: response.status,
      statusText: response.statusText,
      type: response.type,
      url: response.url,
      useFinalURL: (response as any).useFinalURL
    }),
    dateNow: now,
    duration: now - start,
    event: String(response.status),
    level: Level.INFO,
    message: response.statusText,
    metricsState: {
      correlationId,
      sdkVersion
    },
    name: LOGGER_NAME,
    recordType: RecordType.NETWORK_RESPONSE
  } as Metric;

  for (const pair of response.headers.entries()) {
    (m.data as any).headers[pair[0]] = pair[1];
  }
  adobeMetrics.write(m);  return Promise.resolve(response);
}

function instrumentResponseError(start: number, correlationId: string, url: string, fetchArgs: any[], error: any): Promise<any> {
  const now = Date.now();
  const m = {
    data: Object.assign({}, error, {
      description: error.description,
      fetchArgs,
      fileName: error.fileName,
      lineNumber: error.lineNumber,
      message: error.message,
      name: error.name,
      number: error.number,
      stack: error.stack,
      url
    }),
    dateNow: now,
    duration: now - start,
    event: 'Error',
    level: Level.ERROR,
    message: `${error}`,
    metricsState: {
      correlationId,
      sdkVersion
    },
    name: LOGGER_NAME,
    recordType: RecordType.NETWORK_RESPONSE
  } as Metric;
  adobeMetrics.write(m);
  return Promise.reject(error);
}

function invoke(fetch: Function, ...args: any): Promise<any> {
  const correlationId = uuidv1();
  const url = args[0];
  const params = args.slice(1);
  const start = Date.now();
  return Promise.resolve(args)
    .then((args): any => instrumentRequest(start, correlationId, ...args))
    .then((args): any => fetch(...args))
    .then((response): any => instrumentResponse(start, correlationId, response),
      (error): any => instrumentResponseError(start, correlationId, url, params, error));
}

function wrap(fetch: Function): Function {
  return function(...args: any): Promise<any> {
    return invoke(fetch, ...args);
  };
}

/**
 * Initialize the Network interceptor. Currently this just monkey
 * patches (window|global).fetch. Future plans include monkey patch of XHR.
 *
 * @param {string} mode Set explicitly to 'off' to disable the monkeypatch,
 * or set any other value including undefined to enable the monkeypatch.
 */
function init(mode?: string): void {
  const g = global as any;
  switch (mode) {
    case 'off':
      if (g._originalFetch) {
        g.fetch = g._originalFetch;
        delete g._originalFetch;
      }
      break;
    default:
      if (!g._originalFetch && g.fetch) {
        g._originalFetch = g.fetch;
        g.fetch = wrap(g.fetch);
      }
      break;
  }
}

const MetricsNetworkIntercept = {
  init,
  instrumentRequest,
  instrumentResponse,
  instrumentResponseError
};
export default MetricsNetworkIntercept;
